import React from "react";
import {
	alpha,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Paper,
	Typography,
	useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RateReviewTwoToneIcon from "@mui/icons-material/RateReviewTwoTone";
import StarTwoToneIcon from "@mui/icons-material/StarTwoTone";
import dayjs from "dayjs";
import type { IFeedbackItem } from "src/Types/module/feedback/feedback.types";

interface ViewFeedbackModalProps {
	open: boolean;
	handleClose: () => void;
	feedback: IFeedbackItem | null;
}

const ViewFeedbackModal: React.FC<ViewFeedbackModalProps> = ({
	open,
	handleClose,
	feedback,
}) => {
	const theme = useTheme();

	if (!feedback) return null;

	const getUserTypeColor = (type: string): "primary" | "secondary" | "info" | "default" => {
		const lower = (type || "").toLowerCase();
		if (lower === "visitor") return "info";
		if (lower === "exhibitor") return "secondary";
		if (lower === "b2b") return "primary";
		return "default";
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			disableEnforceFocus
			translate="no"
			className="notranslate"
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1.5,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<RateReviewTwoToneIcon color="primary" />
					<Typography variant="h5" fontWeight={600}>
						<span>Feedback #{feedback.id}</span>
					</Typography>
				</Box>
				<IconButton onClick={handleClose} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ pt: 2.5, pb: 2 }}>
				{/* Top Meta info */}
				<Paper
					variant="outlined"
					sx={{
						p: 2,
						mb: 2.5,
						borderRadius: 2,
						bgcolor: alpha(theme.palette.background.default, 0.6),
						display: "flex",
						flexDirection: "column",
						gap: 1.2,
					}}
				>
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Typography variant="caption" color="text.secondary">
								<span>User Type:</span>
							</Typography>
							<Chip
								label={feedback.user_type?.toUpperCase() || "N/A"}
								size="small"
								color={getUserTypeColor(feedback.user_type)}
								sx={{ fontWeight: 600, fontSize: "0.75rem", height: 22 }}
							/>
						</Box>

						{/* Rating Stars */}
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
								<span>Rating:</span>
							</Typography>
							{[1, 2, 3, 4, 5].map((star) => (
								<StarTwoToneIcon
									key={star}
									sx={{
										fontSize: 18,
										color: star <= (feedback.rating || 0) ? "#f59e0b" : "#e2e8f0",
									}}
								/>
							))}
							<Typography variant="body2" fontWeight={700} sx={{ ml: 0.5 }}>
								<span>{feedback.rating}/5</span>
							</Typography>
						</Box>
					</Box>

					{feedback.name && (
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<Typography variant="caption" color="text.secondary">
								<span>Name:</span>
							</Typography>
							<Typography variant="body2" fontWeight={600} color="text.primary">
								<span>{feedback.name}</span>
							</Typography>
						</Box>
					)}

					{feedback.organization && (
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<Typography variant="caption" color="text.secondary">
								<span>Organization:</span>
							</Typography>
							<Typography variant="body2" fontWeight={600} color="text.primary">
								<span>{feedback.organization}</span>
							</Typography>
						</Box>
					)}

					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<Typography variant="caption" color="text.secondary">
							<span>Email:</span>
						</Typography>
						<Typography variant="body2" fontWeight={600} color="text.primary">
							<span>{feedback.user_email || "N/A"}</span>
						</Typography>
					</Box>

					{feedback.phone && (
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<Typography variant="caption" color="text.secondary">
								<span>Phone:</span>
							</Typography>
							<Typography variant="body2" fontWeight={600} color="text.primary">
								<span>{feedback.phone}</span>
							</Typography>
						</Box>
					)}

					{feedback.created_at && (
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<Typography variant="caption" color="text.secondary">
								<span>Submitted Date:</span>
							</Typography>
							<Typography variant="body2" color="text.secondary">
								<span>{dayjs(feedback.created_at).format("DD MMM, YYYY hh:mm A")}</span>
							</Typography>
						</Box>
					)}
				</Paper>

				{/* App Feedback */}
				<Box sx={{ mb: 2.5 }}>
					<Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.8 }}>
						<span>App Feedback</span>
					</Typography>
					<Paper
						variant="outlined"
						sx={{
							p: 2,
							borderRadius: 2,
							bgcolor: alpha(theme.palette.primary.main, 0.02),
							minHeight: "70px",
						}}
					>
						<Typography
							variant="body2"
							color="text.primary"
							sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
						>
							<span>{feedback.app_feedback || "No feedback text provided."}</span>
						</Typography>
					</Paper>
				</Box>

				{/* Improvement Suggestion (if present) */}
				{feedback.improvement_suggestion && (
					<Box sx={{ mb: 1 }}>
						<Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.8 }}>
							<span>Improvement Suggestion</span>
						</Typography>
						<Paper
							variant="outlined"
							sx={{
								p: 2,
								borderRadius: 2,
								bgcolor: alpha(theme.palette.warning.main, 0.04),
								borderColor: alpha(theme.palette.warning.main, 0.2),
							}}
						>
							<Typography
								variant="body2"
								color="text.primary"
								sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
							>
								<span>{feedback.improvement_suggestion}</span>
							</Typography>
						</Paper>
					</Box>
				)}
			</DialogContent>

			<Divider />

			<DialogActions sx={{ p: 2, px: 2.5 }}>
				<Button variant="outlined" color="primary" onClick={handleClose} size="small">
					<span>Close</span>
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ViewFeedbackModal;
